import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  UserPlus, Trash2, Edit2, ShieldCheck, ShieldOff,
  CheckCircle, XCircle, Eye, EyeOff, Loader2, Users, Save, X
} from 'lucide-react';

const ALL_PERMISSIONS = [
  { key: 'add_questions',       label: 'Add Questions',        icon: '➕', color: '#22c55e' },
  { key: 'edit_questions',      label: 'Edit Questions',       icon: '✏️', color: '#3b82f6' },
  { key: 'delete_questions',    label: 'Delete Questions',     icon: '🗑️', color: '#ef4444' },
  { key: 'approve_questions',   label: 'Approve Questions',    icon: '✅', color: '#f59e0b' },
  { key: 'manage_students',     label: 'Manage Students',      icon: '👥', color: '#8b5cf6' },
  { key: 'view_reports',        label: 'View Reports',         icon: '📊', color: '#06b6d4' },
  { key: 'manage_tests',        label: 'Manage Tests',         icon: '📝', color: '#ec4899' },
  { key: 'manage_exam_settings', label: 'Exam Settings',       icon: '⚙️', color: '#f97316' },
];

function PermissionBadge({ permission, granted }) {
  const perm = ALL_PERMISSIONS.find(p => p.key === permission);
  if (!perm) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
      background: granted ? '#fefce8' : '#f9fafb',
      color: granted ? '#854d0e' : '#9ca3af',
      border: `1px solid ${granted ? '#facc15' : '#e5e7eb'}`
    }}>
      <span>{perm.icon}</span> {perm.label}
    </span>
  );
}

function AdminModal({ admin, onClose, onSave }) {
  const isEdit = !!admin;
  const [form, setForm] = useState({
    name: admin?.name || '',
    email: admin?.email || '',
    mobile: admin?.mobile || '',
    password: '',
    isActive: admin?.isActive !== false,
    permissions: admin?.permissions || []
  });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  const togglePermission = (key) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter(p => p !== key)
        : [...f.permissions, key]
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.email) return toast.error('Name and email are required');
    if (!isEdit && !form.password) return toast.error('Password is required for new admin');
    setSaving(true);
    try {
      await onSave({ ...form, password: form.password || undefined });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px'
    }}>
      <div style={{
        background: '#ffffff', borderRadius: '20px', padding: '32px',
        width: '100%', maxWidth: '580px', maxHeight: '90vh',
        overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
        border: '2px solid #facc15'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
              {isEdit ? 'Edit Admin' : 'Create New Admin'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
              {isEdit ? 'Update admin account and permissions' : 'Set up a new admin account with permissions'}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: '#f3f4f6', border: 'none', borderRadius: '10px',
            padding: '8px', cursor: 'pointer', display: 'flex'
          }}>
            <X size={18} color="#374151" />
          </button>
        </div>

        {/* Fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
              Full Name *
            </label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="John Doe"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
              Mobile
            </label>
            <input
              value={form.mobile}
              onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
              placeholder="+91 9876543210"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Email Address *
          </label>
          <input
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="admin@eamcet.com"
            disabled={isEdit}
            style={{ ...inputStyle, background: isEdit ? '#f9fafb' : '#ffffff', color: isEdit ? '#6b7280' : '#111827' }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            {isEdit ? 'New Password (leave blank to keep current)' : 'Password *'}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPwd ? 'text' : 'password'}
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder={isEdit ? 'Enter new password to change' : 'Min 8 characters'}
              style={{ ...inputStyle, paddingRight: '40px' }}
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)} style={{
              position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', display: 'flex', color: '#9ca3af'
            }}>
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* Permissions */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
            Assign Permissions
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {ALL_PERMISSIONS.map(perm => {
              const granted = form.permissions.includes(perm.key);
              return (
                <button
                  key={perm.key}
                  type="button"
                  onClick={() => togglePermission(perm.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 12px', borderRadius: '12px', cursor: 'pointer',
                    border: `2px solid ${granted ? '#facc15' : '#e5e7eb'}`,
                    background: granted ? '#fefce8' : '#ffffff',
                    transition: 'all 0.15s', textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{perm.icon}</span>
                  <span style={{
                    fontSize: '12px', fontWeight: '600',
                    color: granted ? '#854d0e' : '#6b7280', flex: 1
                  }}>
                    {perm.label}
                  </span>
                  {granted
                    ? <CheckCircle size={14} color="#ca8a04" />
                    : <XCircle size={14} color="#d1d5db" />
                  }
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setForm(f => ({
              ...f,
              permissions: f.permissions.length === ALL_PERMISSIONS.length
                ? []
                : ALL_PERMISSIONS.map(p => p.key)
            }))}
            style={{
              marginTop: '10px', fontSize: '12px', color: '#ca8a04',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: '600'
            }}
          >
            {form.permissions.length === ALL_PERMISSIONS.length ? 'Remove All Permissions' : 'Grant All Permissions'}
          </button>
        </div>

        {/* Active toggle (edit only) */}
        {isEdit && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', background: '#fefce8', borderRadius: '12px', marginBottom: '24px',
            border: '1px solid #fde68a'
          }}>
            <div>
              <p style={{ margin: 0, fontWeight: '600', fontSize: '13px', color: '#374151' }}>Account Status</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
                {form.isActive ? 'Active — Admin can log in' : 'Disabled — Admin cannot log in'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
              style={{
                width: '48px', height: '26px', borderRadius: '13px',
                background: form.isActive ? '#facc15' : '#d1d5db',
                border: 'none', cursor: 'pointer', position: 'relative',
                transition: 'background 0.2s'
              }}
            >
              <div style={{
                position: 'absolute', top: '3px',
                left: form.isActive ? '24px' : '3px',
                width: '20px', height: '20px',
                background: '#111827', borderRadius: '50%',
                transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
              }} />
            </button>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '12px', borderRadius: '12px',
            background: '#f3f4f6', border: 'none', cursor: 'pointer',
            fontWeight: '600', fontSize: '14px', color: '#374151'
          }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '8px', padding: '12px', borderRadius: '12px',
            background: saving ? '#fde047' : 'linear-gradient(135deg, #facc15, #eab308)',
            border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
            color: '#111827', fontWeight: '700', fontSize: '14px'
          }}>
            {saving && <Loader2 size={16} className="animate-spin" />}
            {saving ? 'Saving...' : <><Save size={16} /> {isEdit ? 'Save Changes' : 'Create Admin'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '10px 12px',
  border: '1px solid #d1d5db', borderRadius: '10px',
  fontSize: '14px', color: '#111827', background: '#ffffff',
  outline: 'none', transition: 'border-color 0.2s'
};

export default function SuperAdminPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | adminObject
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchAdmins(); }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/admins');
      setAdmins(data.data);
    } catch (err) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (form) => {
    const { data } = await api.post('/admin/admins', form);
    toast.success('Admin created successfully!');
    setAdmins(prev => [data.data, ...prev]);
  };

  const handleUpdate = async (form) => {
    const { data } = await api.put(`/admin/admins/${modal._id}`, form);
    toast.success('Admin updated!');
    setAdmins(prev => prev.map(a => a._id === modal._id ? data.data : a));
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/admins/${id}`);
      toast.success('Admin deleted');
      setAdmins(prev => prev.filter(a => a._id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await api.put(`/admin/admins/${admin._id}`, { isActive: !admin.isActive });
      toast.success(`Admin ${admin.isActive ? 'disabled' : 'enabled'}`);
      setAdmins(prev => prev.map(a => a._id === admin._id ? { ...a, isActive: !a.isActive } : a));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' }}>
            Admin Management
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
            Create, manage, and configure admin accounts and permissions
          </p>
        </div>
        <button
          id="create-admin-btn"
          onClick={() => setModal('create')}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 20px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #facc15, #eab308)',
            border: 'none', cursor: 'pointer',
            color: '#111827', fontWeight: '700', fontSize: '14px',
            boxShadow: '0 4px 12px rgba(250, 204, 21, 0.3)'
          }}
        >
          <UserPlus size={18} /> Create Admin
        </button>
      </div>

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Admins', value: admins.length, color: '#111827', bg: '#fefce8', border: '#facc15' },
          { label: 'Active', value: admins.filter(a => a.isActive).length, color: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
          { label: 'Disabled', value: admins.filter(a => !a.isActive).length, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
        ].map(({ label, value, color, bg, border }) => (
          <div key={label} style={{
            background: bg, borderRadius: '14px', padding: '16px 24px',
            border: `2px solid ${border}`
          }}>
            <p style={{ margin: 0, fontSize: '22px', fontWeight: '700', color }}>{value}</p>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Admin cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
          <Loader2 size={32} className="animate-spin" style={{ margin: '0 auto 12px', display: 'block' }} />
          <p>Loading admins...</p>
        </div>
      ) : admins.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px',
          background: '#fefce8', borderRadius: '16px', border: '2px dashed #facc15'
        }}>
          <Users size={48} color="#ca8a04" style={{ margin: '0 auto 12px' }} />
          <p style={{ color: '#854d0e', fontSize: '16px', fontWeight: '500' }}>No admin accounts yet</p>
          <p style={{ color: '#ca8a04', fontSize: '13px' }}>Click "Create Admin" to add your first admin</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {admins.map(admin => (
            <div key={admin._id} style={{
              background: '#ffffff', borderRadius: '16px', padding: '20px 24px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
              opacity: admin.isActive ? 1 : 0.7,
              transition: 'all 0.2s ease'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                {/* Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                    background: 'linear-gradient(135deg, #facc15, #eab308)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#111827', fontWeight: '700', fontSize: '18px'
                  }}>
                    {admin.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontWeight: '700', fontSize: '15px', color: '#111827' }}>
                        {admin.name}
                      </p>
                      <span style={{
                        padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                        background: admin.isActive ? '#f0fdf4' : '#fef2f2',
                        color: admin.isActive ? '#16a34a' : '#dc2626',
                        border: `1px solid ${admin.isActive ? '#bbf7d0' : '#fecaca'}`
                      }}>
                        {admin.isActive ? '● Active' : '● Disabled'}
                      </span>
                    </div>
                    <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6b7280' }}>{admin.email}</p>
                    {admin.mobile && (
                      <p style={{ margin: '1px 0 0', fontSize: '12px', color: '#9ca3af' }}>{admin.mobile}</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => handleToggleActive(admin)}
                    title={admin.isActive ? 'Disable admin' : 'Enable admin'}
                    style={{
                      padding: '8px', borderRadius: '10px',
                      background: admin.isActive ? '#fef2f2' : '#f0fdf4',
                      border: 'none', cursor: 'pointer', display: 'flex'
                    }}
                  >
                    {admin.isActive ? <ShieldOff size={16} color="#ef4444" /> : <ShieldCheck size={16} color="#22c55e" />}
                  </button>
                  <button
                    onClick={() => setModal(admin)}
                    title="Edit admin"
                    style={{
                      padding: '8px', borderRadius: '10px',
                      background: '#fefce8', border: '1px solid #facc15', cursor: 'pointer', display: 'flex'
                    }}
                  >
                    <Edit2 size={16} color="#ca8a04" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(admin)}
                    title="Delete admin"
                    style={{
                      padding: '8px', borderRadius: '10px',
                      background: '#fef2f2', border: '1px solid #fecaca', cursor: 'pointer', display: 'flex'
                    }}
                  >
                    <Trash2 size={16} color="#ef4444" />
                  </button>
                </div>
              </div>

              {/* Permissions */}
              <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #f3f4f6' }}>
                <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Permissions ({admin.permissions?.length || 0}/{ALL_PERMISSIONS.length})
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {ALL_PERMISSIONS.map(perm => (
                    <PermissionBadge
                      key={perm.key}
                      permission={perm.key}
                      granted={(admin.permissions || []).includes(perm.key)}
                    />
                  ))}
                </div>
              </div>

              {/* Created by */}
              {admin.createdBy && (
                <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#9ca3af' }}>
                  Created by {admin.createdBy.name} · {new Date(admin.createdAt).toLocaleDateString()}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {modal && (
        <AdminModal
          admin={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSave={modal === 'create' ? handleCreate : handleUpdate}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '20px', padding: '32px',
            width: '100%', maxWidth: '400px', boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
            border: '2px solid #facc15'
          }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '16px',
              background: '#fefce8', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 20px',
              border: '1px solid #fde68a'
            }}>
              <Trash2 size={28} color="#ca8a04" />
            </div>
            <h3 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: '#111827' }}>
              Delete Admin?
            </h3>
            <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', margin: '0 0 24px' }}>
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{
                flex: 1, padding: '12px', borderRadius: '12px',
                background: '#f3f4f6', border: 'none', cursor: 'pointer',
                fontWeight: '600', fontSize: '14px', color: '#374151'
              }}>
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm._id)} style={{
                flex: 1, padding: '12px', borderRadius: '12px',
                background: 'linear-gradient(135deg, #facc15, #eab308)', border: 'none', cursor: 'pointer',
                color: '#111827', fontWeight: '700', fontSize: '14px'
              }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
